import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatAxiosError } from "@/lib/api";
import { useCapabilities } from "@/context/CapabilitiesContext";
import { Briefcase, Plus, MapPin, Buildings, Users, UserPlus, ListChecks } from "@phosphor-icons/react";

const STATUS_COLORS = {
  draft: "bg-gray-100 text-gray-700 border-gray-300",
  open: "bg-green-100 text-green-800 border-green-400",
  closed: "bg-red-100 text-red-800 border-red-300",
  on_hold: "bg-amber-100 text-amber-800 border-amber-300",
};

export default function Jobs() {
  const { can } = useCapabilities();
  const nav = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [counts, setCounts] = useState({});
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [dept, setDept] = useState("");
  const [loading, setLoading] = useState(false);
  const [referFor, setReferFor] = useState(null); // job object

  const load = async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams();
      if (q) p.set("q", q);
      if (status) p.set("status", status);
      if (dept) p.set("department", dept);
      const r = await api.get(`/jobs?${p}`);
      const list = r.data.jobs || [];
      setJobs(list);
      // Fetch applicant counts in parallel (best-effort)
      if (can("use_jobs")) {
        const counts = {};
        await Promise.all(list.map(async (j) => {
          try {
            const ar = await api.get(`/jobs/${j.id}/applications`);
            counts[j.id] = ar.data.total || 0;
          } catch { counts[j.id] = null; }
        }));
        setCounts(counts);
      }
    } catch { /* ignore */ }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [q, status, dept]);

  return (
    <div className="p-8 lg:p-12">
      <div className="mb-8">
        <div className="eyebrow mb-2 flex items-center gap-2"><Briefcase size={12} weight="bold" /> 03 / Talent</div>
        <div className="flex flex-wrap items-center justify-between gap-4">
          <h1 className="font-display text-4xl font-black tracking-tight">Job Openings</h1>
          {can("manage_jobs") && (
            <Link to="/jobs/new" className="btn-primary flex items-center gap-2" data-testid="new-job-btn">
              <Plus size={14} weight="bold" /> Post a role
            </Link>
          )}
        </div>
        <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
          Create, document, and manage open positions. Start from a template or a blank slate.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-6">
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search title, description…" className="border border-border px-3 py-1.5 text-sm bg-white min-w-[280px]" data-testid="jobs-search" />
        <select value={status} onChange={(e) => setStatus(e.target.value)} className="border border-border px-3 py-1.5 text-sm bg-white" data-testid="jobs-filter-status">
          <option value="">All statuses</option>
          <option value="draft">Draft</option>
          <option value="open">Open</option>
          <option value="on_hold">On hold</option>
          <option value="closed">Closed</option>
        </select>
        <input value={dept} onChange={(e) => setDept(e.target.value)} placeholder="Department" className="border border-border px-3 py-1.5 text-sm bg-white" data-testid="jobs-filter-dept" />
      </div>

      {loading ? <div className="text-sm text-muted-foreground">Loading…</div>
       : jobs.length === 0 ? (
        <div className="border border-dashed border-border p-12 text-center text-muted-foreground">
          No jobs yet.{can("manage_jobs") && <> Click <b>Post a role</b> to create your first opening.</>}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {jobs.map(j => (
            <div key={j.id} className="border border-border p-5 hover:border-[#002FA7] hover:shadow-md transition-all bg-white flex flex-col" data-testid={`job-card-${j.id}`}>
              <button onClick={() => nav(`/jobs/${j.id}`)} className="text-left flex-1">
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div className="font-display text-lg font-black leading-tight">{j.title}</div>
                  <span className={`shrink-0 px-2 py-0.5 text-[10px] font-semibold uppercase border ${STATUS_COLORS[j.status] || ""}`}>{j.status}</span>
                </div>
                <div className="space-y-1 text-xs text-muted-foreground">
                  {j.department && <div className="flex items-center gap-1"><Buildings size={12} weight="bold" /> {j.department}</div>}
                  {j.location && <div className="flex items-center gap-1"><MapPin size={12} weight="bold" /> {j.location}</div>}
                  <div className="flex items-center gap-1"><Users size={12} weight="bold" /> {j.employment_type} · {j.experience_level}</div>
                </div>
                {j.skills && j.skills.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-1">
                    {j.skills.slice(0, 5).map((s, i) => (
                      <span key={i} className="text-[10px] px-1.5 py-0.5 bg-muted border border-border">{s}</span>
                    ))}
                  </div>
                )}
                <div className="mt-3 text-[10px] text-muted-foreground">Created {new Date(j.created_at).toLocaleDateString()}</div>
              </button>
              <div className="mt-3 pt-3 border-t border-border flex flex-wrap gap-2">
                <button
                  onClick={(e) => { e.stopPropagation(); nav(`/jobs/${j.id}/applicants`); }}
                  className="flex items-center gap-1 text-xs px-2.5 py-1.5 border border-border hover:bg-muted"
                  data-testid={`view-applicants-${j.id}`}>
                  <ListChecks size={12} weight="bold" />
                  Applicants{counts[j.id] != null ? ` (${counts[j.id]})` : ""}
                </button>
                {can("use_referrals") && j.status === "open" && (
                  <button
                    onClick={(e) => { e.stopPropagation(); setReferFor(j); }}
                    className="flex items-center gap-1 text-xs px-2.5 py-1.5 border border-[#002FA7] text-[#002FA7] hover:bg-[#002FA7] hover:text-white"
                    data-testid={`refer-btn-${j.id}`}>
                    <UserPlus size={12} weight="bold" /> Refer
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
      {referFor && <ReferForJobModal job={referFor} onClose={() => setReferFor(null)} onDone={() => { setReferFor(null); load(); }} />}
    </div>
  );
}

function ReferForJobModal({ job, onClose, onDone }) {
  const [form, setForm] = useState({
    candidate_name: "", candidate_email: "", candidate_phone: "",
    resume_file_id: "", attachments: [], notes: "",
  });
  const [resumeMeta, setResumeMeta] = useState(null);
  const [attMetas, setAttMetas] = useState([]);
  const [uploadingR, setUploadingR] = useState(false);
  const [uploadingA, setUploadingA] = useState(false);
  const [err, setErr] = useState("");
  const [ok, setOk] = useState("");
  const [busy, setBusy] = useState(false);

  const upload = async (file, setBusyU, cb) => {
    setBusyU(true); setErr("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/uploads/file", fd, { headers: { "Content-Type": "multipart/form-data" } });
      cb(r.data);
    } catch (e) { setErr(formatAxiosError(e)); }
    finally { setBusyU(false); }
  };

  const humanSize = (b) => {
    if (b == null) return "";
    const u = ["B", "KB", "MB"]; let i = 0, n = b;
    while (n >= 1024 && i < u.length - 1) { n /= 1024; i++; }
    return `${n.toFixed(n >= 10 || i === 0 ? 0 : 1)} ${u[i]}`;
  };

  const save = async () => {
    setBusy(true); setErr(""); setOk("");
    try {
      await api.post("/referrals", {
        candidate_name: form.candidate_name.trim(),
        candidate_email: form.candidate_email.trim(),
        candidate_phone: form.candidate_phone.trim(),
        position: job.title,
        job_id: job.id,
        resume_file_id: form.resume_file_id || "",
        attachments: form.attachments,
        notes: form.notes.trim(),
      });
      setOk("Referral submitted!");
      setTimeout(() => onDone(), 900);
    } catch (e) { setErr(formatAxiosError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white border border-border max-w-xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-border flex items-center justify-between">
          <div>
            <div className="text-xs uppercase tracking-widest text-muted-foreground">Refer a candidate</div>
            <h3 className="font-display text-lg font-black">For: {job.title}</h3>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-muted">✕</button>
        </div>
        <div className="p-6 space-y-3">
          {err && <div className="text-xs text-red-700 border border-red-500 bg-red-50 px-3 py-2">{err}</div>}
          {ok && <div className="text-xs text-green-700 border border-green-500 bg-green-50 px-3 py-2">{ok}</div>}
          <div className="grid grid-cols-2 gap-3">
            <FieldSmall label="Candidate name*"><input className="input" value={form.candidate_name} onChange={(e) => setForm({ ...form, candidate_name: e.target.value })} data-testid="jrefer-name" /></FieldSmall>
            <FieldSmall label="Email*"><input className="input" type="email" value={form.candidate_email} onChange={(e) => setForm({ ...form, candidate_email: e.target.value })} data-testid="jrefer-email" /></FieldSmall>
            <FieldSmall label="Phone"><input className="input" value={form.candidate_phone} onChange={(e) => setForm({ ...form, candidate_phone: e.target.value })} /></FieldSmall>
          </div>
          <FieldSmall label="Resume">
            {resumeMeta ? (
              <div className="flex items-center gap-2 border border-border px-3 py-2 bg-muted/30 text-sm">
                <span className="flex-1 truncate">{resumeMeta.filename}</span>
                <span className="text-xs text-muted-foreground">{humanSize(resumeMeta.size_bytes)}</span>
                <button onClick={() => { setResumeMeta(null); setForm(f => ({ ...f, resume_file_id: "" })); }} className="text-red-600 text-xs">Remove</button>
              </div>
            ) : (
              <label className="flex items-center gap-2 border border-dashed border-border px-3 py-3 hover:bg-muted/30 cursor-pointer text-sm">
                <span>{uploadingR ? "Uploading resume…" : "📄 Upload resume"}</span>
                <input type="file" className="hidden" accept=".pdf,.doc,.docx,.rtf,.txt" onChange={(e) => {
                  const f = e.target.files && e.target.files[0]; if (!f) return;
                  upload(f, setUploadingR, (d) => {
                    const fid = d.file_id || d.id;
                    setForm(x => ({ ...x, resume_file_id: fid }));
                    setResumeMeta({ file_id: fid, filename: d.filename || f.name, size_bytes: d.size_bytes || f.size });
                  });
                }} data-testid="jrefer-resume" />
              </label>
            )}
          </FieldSmall>
          <FieldSmall label="Other documents">
            {attMetas.length > 0 && (
              <ul className="space-y-1 mb-2">
                {attMetas.map(m => (
                  <li key={m.file_id} className="flex items-center gap-2 border border-border px-3 py-1.5 bg-muted/30 text-xs">
                    <span className="flex-1 truncate">{m.filename}</span>
                    <span className="text-muted-foreground">{humanSize(m.size_bytes)}</span>
                    <button onClick={() => { setAttMetas(p => p.filter(x => x.file_id !== m.file_id)); setForm(x => ({ ...x, attachments: x.attachments.filter(a => a !== m.file_id) })); }} className="text-red-600">Remove</button>
                  </li>
                ))}
              </ul>
            )}
            <label className="flex items-center gap-2 border border-dashed border-border px-3 py-2 hover:bg-muted/30 cursor-pointer text-sm">
              <span>{uploadingA ? "Uploading…" : "📎 Add another document"}</span>
              <input type="file" className="hidden" onChange={(e) => {
                const f = e.target.files && e.target.files[0]; if (!f) return;
                upload(f, setUploadingA, (d) => {
                  const fid = d.file_id || d.id;
                  setForm(x => ({ ...x, attachments: [...x.attachments, fid] }));
                  setAttMetas(p => [...p, { file_id: fid, filename: d.filename || f.name, size_bytes: d.size_bytes || f.size }]);
                });
                e.target.value = "";
              }} />
            </label>
          </FieldSmall>
          <FieldSmall label="Notes"><textarea className="input min-h-[80px]" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></FieldSmall>
        </div>
        <div className="px-6 py-4 border-t border-border flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm border border-border">Cancel</button>
          <button onClick={save} disabled={busy} className="btn-primary" data-testid="jrefer-save">{busy ? "Sending…" : "Submit referral"}</button>
        </div>
      </div>
    </div>
  );
}

function FieldSmall({ label, children }) {
  return (
    <div className="block">
      <span className="block text-xs font-mono-mini uppercase tracking-widest text-muted-foreground mb-1">{label}</span>
      {children}
    </div>
  );
}
